import React, { useEffect, lazy, Suspense, useCallback } from "react";
import { ThemeProvider, CssBaseline, useMediaQuery, useTheme } from "@mui/material";

import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection/HeroSection";
import StickySectionHeader from "./components/StickySectionHeader";
import TalkToOurConsultant from "./components/HeroSection/TalkToOurConsultant";
import FreeConsultationForm from "./components/FreeConsultationForm";
import DevOpsHighlightSection from "./components/DevOpsHighlightSection";

import FloatingButtons from "./components/FloatingButtons";
import Footer from "./components/Footer";
import StickyFooter from "./components/StickyFooter";
import { DEVOPS_CERTIFICATION, EXECUTIVE_PG_DEVOPS_PROGRAM, EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT } from "../constants/AzureDevops";
import { Helmet } from "react-helmet-async";
import PricingComparison from "./components/PricingComparison";
import AdmissionBanner from "./components/AdmissionBanner";
import TrustAndRatingsSection from "./components/TrustAndRatingsSection";
import HeroSectionTihIit from "./components/HeroSection/HeroSectionTihIit";
import SuccessStoriesV3 from "../DevopsV3/components/SuccessStoriesV3";
import { defaultFaqs, executiveFaqs, cityFaqs } from "./data/devopsFaqs";
import StatsSectionV3 from "../DevopsV3/components/StatsSectionV3";



/* Lazy loaded sections */
const ThousandAlumni = lazy(() => import("./components/ThousandAlumni"));
const BootcampHighlights = lazy(() => import("./components/BootcampHighlights"));
const BootcampCurriculum = lazy(() => import("./components/BootcampCurriculum"));
const JourneySection = lazy(() => import("./components/JourneySection"));
const CompanyLogos = lazy(() => import("../components/CompanyLogos"));
const MultiCloudExpert = lazy(() => import("./components/MultiCloudExpert"));
const ToolsToMaster = lazy(() => import("./components/ToolsToMaster"));
const GainFromProgram = lazy(() => import("./components/GainFromProgram"));
const CareerSection = lazy(() => import("./components/CareerSection"));
const PaperLiveLearningSection = lazy(() => import("./components/PaperLiveLearningSection"));
const BootcampPricing = lazy(() => import("./components/BootcampPricing"));
const BatchDetails = lazy(() => import("./components/BatchDetails"));
const DiscountSection = lazy(() => import("./components/DiscountSection"));
const V2InstructorContent = lazy(() => import("./components/InstructorScrollable/v2InstructorContent"));
const YouTubeVideoCarousel = lazy(() => import("./components/YouTubeVideoCarousel"));
const CareerAssistanceSection = lazy(() => import("./components/CareerAssistanceSection"));
const MilestoneSection = lazy(() => import("./components/MilestoneSection"));
const ProjectDetails = lazy(() => import("./components/ProjectDetails"));
const CaseStudySlider = lazy(() => import("./components/CaseStudySlider"));
const PrerequisitesEligibility = lazy(() => import("./components/PrerequisitesEligibility"));
const CollaborationMicrosoft = lazy(() => import("./components/CollaborationMicrosoft"));
const CloudCertifications = lazy(() => import("./components/CloudCertifications"));
const IndustryRecognizedCertifications = lazy(() => import("./components/IndustryRecognizedCertifications"));
const AwardsRecognition = lazy(() => import("./components/AwardsRecognition"));
const ReferAndEarn = lazy(() => import("./components/ReferAndEarn"));
const ReviewsSection = lazy(() => import("./components/ReviewsSection"));
const GoogleReviews = lazy(() => import("./components/GoogleReviews"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const StillNotSure = lazy(() => import("./components/StillNotSure"));

const DevopsV2Main = (props) => {
  const { city, course } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const isCertification = course === DEVOPS_CERTIFICATION;
  const isExecutive = course === EXECUTIVE_PG_DEVOPS_PROGRAM || course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT;

  /* Course Name */
  const courseName = isCertification
    ? "DevOps Certification Training Job-Ready Program"
    : isExecutive
      ? `${"DevOps Course Online | In Collaboration with Microsoft & AWS"}${city ? ` in ${city}` : ""}`
      : city
        ? `DevOps & Cloud with Gen AI Job-Ready Program in ${city}`
        : "DevOps & Cloud with Gen AI Job-Ready Program";

  /* Description */
  const courseDescription = isCertification
    ? "Advance your career with our DevOps Certification Training with hands-on projects and real-world tools."
    : isExecutive
      ? `Master DevOps with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft & AWS and accelerate your tech career`
      : `Join one of the best DevOps courses ${city ? `in ${city}` : ""} with hands-on tools, projects, and job-ready training.`;

  /* Dynamic price */
  const price = isExecutive ? "59999" : isCertification ? "49999" : "59999";

  /* Dynamic image */
  const image = isExecutive
    ? "https://paperlive.in/static/media/HeroImageIITPatna.48e3a58e629c15120cda.webp"
    : "https://lh3.googleusercontent.com/glsgmb/AMnFcupyBF0xZDJ8Wjjk8VPpZeX_30zkizfBF7kUsH6qbZDa9JNCIASYJSpCJrqskN0GlI5_qUicqkI_WqcMg8hdELNzPQ=w608-h1081-k-rw-no-sc0x00ffffff";

  /* Smooth scroll */
  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  /* Preload lazy components in background */
  useEffect(() => {
    import("./components/ThousandAlumni");
    import("./components/BootcampHighlights");
    import("./components/BootcampCurriculum");
    import("./components/JourneySection");
    import("./components/MultiCloudExpert");
    import("./components/ToolsToMaster");
    import("./components/GainFromProgram");
    import("./components/CareerSection");
    import("./components/PaperLiveLearningSection");
    import("./components/BootcampPricing");
    import("./components/BatchDetails");
    import("./components/DiscountSection");
    import("./components/InstructorScrollable/v2InstructorContent");
    import("./components/ProjectDetails");
    import("./components/ReviewsSection");
    import("./components/FAQSection");
  }, []);


  /* Reviews shown on-page in ReviewsSection/GoogleReviews — mirrored here so
     structured data matches visible content per Google's review-snippet policy. */
  const courseReviews = [
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Nill Singh" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2025-11-01",
      "reviewBody": "The PaperLive DevOps training helped me move from basic knowledge to mastering tools like Kubernetes, Terraform, and CI/CD pipelines. The instructors are professionals from top companies and make learning easy with real projects."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Dishari" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2025-09-01",
      "reviewBody": "This course is a complete package for DevOps learners. PaperLive Learning covers everything from CI/CD automation to managing cloud infrastructure. The real-time project submissions and timely assessments helped me stay on track."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Nim Dawa Sherpa" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2025-09-01",
      "reviewBody": "PaperLive Learning helped me make a successful career shift into cloud engineering. The course is intense but rewarding, filled with practicals, quizzes, and resume-building sessions. Every doubt was answered patiently, and I now feel job-ready with my DevOps skills."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Arko Mitra" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2026-04-01",
      "reviewBody": "I joined Paperlive Learning to strengthen my DevOps skills, and the hands-on training really made a difference. The mock interviews and real-time projects helped me gain confidence and clarity."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Iswarya T" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2026-04-01",
      "reviewBody": "I gained valuable knowledge through this platform. The learning environment is very supportive, and the guidance provided helped me a lot throughout my journey. Because of Paperlive, I was able to secure a placement, which I'm truly grateful for."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Ashish kumar jha" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2026-01-01",
      "reviewBody": "I recently completed the DevOps program with placement support from Paperlive Learning and overall it was a really positive experience for me. The way the course was structured made it easy to follow, everything was explained step by step, and the trainers were patient and knowledgeable."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Mabiya Parween" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2025-10-01",
      "reviewBody": "DevOps became easy with PaperLive Learning's structured approach. The mentors simplified every tool with practical examples. I now have the confidence to handle real projects effectively."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Sajid Shahjad" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2026-07-01",
      "reviewBody": "One of the best platforms to learn DevOps and cloud. The hands-on labs made a big difference. While enrolling, I got the best deal in comparison to other platforms."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Ayush Kumar" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2026-01-01",
      "reviewBody": "I recently completed the Cloud and DevOps training program from Paperlive Learning and it was a great learning experience. The course was very well structured and covered all major DevOps tools and cloud concepts like Docker, Kubernetes, Jenkins, Terraform, and AWS."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Yuvanandhan R" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2026-04-01",
      "reviewBody": "I started my journey in Paperlive as a fresher. Many said it's impossible for a fresher to learn and get placed in the field of DevOps. But with the consistent training and mentorship from Paperlive Learning I really cracked my interview and got placed within the given period of time."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Rekha Deogharia" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2025-12-01",
      "reviewBody": "I found PaperLive Learning while looking for affordable DevOps training. Trainers knew their stuff, the sessions were interactive, and the placement team was with me until I got interview calls."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Anjum Firadaush" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2025-11-01",
      "reviewBody": "What makes PaperLive Learning stand out is their dedication to job-focused education. We didn't just learn AWS or Azure, we actually built real-time apps and pipelines. I loved the mix of DevOps with GenAI, it gave me an edge."
    },
    {
      "@type": "Review",
      "author": { "@type": "Person", "name": "Tanweer Alam" },
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "datePublished": "2025-10-01",
      "reviewBody": "PaperLive Learning is an excellent online platform for anyone looking to upgrade their tech skills through practical, hands-on training, especially in high-demand fields like DevOps, Cloud Computing, and Web Development."
    }
  ];

  const aggregateRating = {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "bestRating": "5",
    "reviewCount": "1000"
  };

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": courseName,
    "description": courseDescription,
    "provider": {
      "@type": "Organization",
      "name": "Paperlive Learning",
      "sameAs": "https://paperlive.in"
    },
    "courseMode": "Online",
    "inLanguage": "en",
    "educationalCredentialAwarded": isExecutive
      ? "Microsoft & AWS Certification"
      : "Certificate of Completion",
    "aggregateRating": aggregateRating,
    "review": courseReviews
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": courseName,
    "description": courseDescription,
    "image": image,
    "brand": {
      "@type": "Brand",
      "name": "Paperlive Learning"
    },
    "offers": {
      "@type": "Offer",
      "url": `https://paperlive.in/devops-training-online${city ? `-${city.toLowerCase()}` : ""}`,
      "priceCurrency": "INR",
      "price": price,
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": aggregateRating,
    "review": courseReviews
  };

  /* Pick correct FAQ set */
  const selectedFaqs = (city && cityFaqs[city]) || (isExecutive ? executiveFaqs : defaultFaqs);

  /* Convert to schema.org format */
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": selectedFaqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question.replace(/^Q\d+\.\s*/, ""),
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <Navbar isSmallScreen={isSmallScreen} scrollToSection={scrollToSection} course={course} />

        <div id="hero">
          <HeroSectionTihIit isSmallScreen={isSmallScreen} city={city} course={course} />
        </div>

        <StickySectionHeader scrollToSection={scrollToSection} city={city} course={course} />

        {/* <div id="consultant">
          <TalkToOurConsultant isSmallScreen={isSmallScreen} scrollToSection={scrollToSection} city={city} course={course} />
        </div> */}
        {/* {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="free-consultation">
          <FreeConsultationForm isSmallScreen={isSmallScreen} scrollToSection={scrollToSection} city={city} course={course} />
        </div>} */}

        {/* {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="devops-highlight">
          <DevOpsHighlightSection isSmallScreen={isSmallScreen} scrollToSection={scrollToSection} city={city} course={course} />
        </div>} */}
          
        {(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <TrustAndRatingsSection isSmallScreen={isSmallScreen} scrollToSection={scrollToSection} city={city} course={course} />  }
        <Suspense fallback={<div />}>
          <div id="alumni"><StatsSectionV3 isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          <div id="bootcamp-highlights"><BootcampHighlights isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          <div id="curriculum"><BootcampCurriculum isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          {!(course === DEVOPS_CERTIFICATION) &&
            <div id="journey"><JourneySection isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          }
          <div id="companylogos"><CompanyLogos isSmallScreen={isSmallScreen} websitePage="DevopsV2" city={city} course={course} /></div>
          {!(course === DEVOPS_CERTIFICATION) &&
            <div id="multicloud"><MultiCloudExpert isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          }
          <div id="tools"><ToolsToMaster isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="gain"><GainFromProgram isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          <div id="career"><CareerSection isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="paperlive"><PaperLiveLearningSection isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          <div id="pricing">{course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT ?
            <PricingComparison scrollToSection={scrollToSection}/> :
            <BootcampPricing isSmallScreen={isSmallScreen} city={city} course={course} scrollToSection={scrollToSection} />
          }</div>
          <div id="batch"><BatchDetails isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="discount"><DiscountSection isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="instructors"><V2InstructorContent isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          <div id="youtube"><SuccessStoriesV3 isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="career-assistance"><CareerAssistanceSection isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="milestones"><MilestoneSection isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="projects"><ProjectDetails isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="case-studies"><CaseStudySlider isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="prerequisites"><PrerequisitesEligibility isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="collaboration"><CollaborationMicrosoft isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          {(course === EXECUTIVE_PG_DEVOPS_PROGRAM || course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="cloud-certifications"><CloudCertifications isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          <div id={course === EXECUTIVE_PG_DEVOPS_PROGRAM || course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT ? "paperlive-certifications" : "certifications"}><IndustryRecognizedCertifications isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="awards"><AwardsRecognition isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="refer"><ReferAndEarn isSmallScreen={isSmallScreen} city={city} course={course} /></div>}
          <div id="reviews"><ReviewsSection isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          <div id="google-reviews"><GoogleReviews isSmallScreen={isSmallScreen} city={city} course={course} /></div>
          {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <div id="faq"><FAQSection isSmallScreen={isSmallScreen} city={city} course={course} defaultFaqs={defaultFaqs} executiveFaqs={executiveFaqs} cityFaqs={cityFaqs}/></div>}
          <div id="still-not-sure"><StillNotSure isSmallScreen={isSmallScreen} city={city} course={course} /></div>
        </Suspense>

        {!(course === EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT) && <Footer scrollToSection={scrollToSection} />}

        {/* {!isSmallScreen && <FloatingButtons />} */}

        <StickyFooter />
      </ThemeProvider>
    </>
  );
};

export default DevopsV2Main;